import { FunctionComponent, useState } from "react"
import { FormI } from "./types"
import ModalFooter from "../shared/modalFooter"
import Alert from "../shared/alert"
import Form from "./form"
import { useOrganizationMembershipAccessForm } from "./hook"
import { getWellPermissionsSetInput, isSubmitFormDisabled } from "./api"
import { SubjectRole } from "../../generated/graphql"
import { useMutation } from "@apollo/client"
import { WELL_PERMISSIONS_SET } from "../../graphql/mutations/well_permissions_set"
import { useNavigate } from "react-router-dom"
import ConfirmationModal from "./confirmationModal"

interface OrganizationMembershipAccessUpdateFormContainerProps {
    initialForm: FormI
    targetSubjectName: string
    targetSubjectRole: SubjectRole
}

const FormContainer: FunctionComponent<
    OrganizationMembershipAccessUpdateFormContainerProps
> = ({ initialForm, targetSubjectName, targetSubjectRole }) => {
    // hooks
    const navigate = useNavigate()

    // state
    const {
        form,
        handleManagedAccessChange,
        handleWellPermissionsChange,
        handleSelectAllWells,
    } = useOrganizationMembershipAccessForm(initialForm)
    const [errorBanner, setErrorBanner] = useState({
        error: false,
        message: "",
    })
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)

    // mutation
    const [wellPermissionsSet, { loading: wellPermissionsSetLoading }] =
        useMutation(WELL_PERMISSIONS_SET)

    // handleSubmit
    const handleSubmitMutation = async () => {
        // No form validation or sanitization necessary
        try {
            await wellPermissionsSet({
                variables: {
                    WellPermissionsSetInput: getWellPermissionsSetInput(form),
                },
            })
            // Navigate back to users admin table if successful
            navigate(-1)
        } catch (error) {
            setErrorBanner({
                error: true,
                message: `There was a problem completing the request. ${targetSubjectName}'s access status and well permissions didn't update. Please reopen the modal and complete the process again.`,
            })
            console.error(error)
        }
        setShowConfirmationModal(false)
    }

    const isLoading = wellPermissionsSetLoading

    // constants
    const disableSubmit = isSubmitFormDisabled(
        initialForm,
        form,
        errorBanner.error,
        isLoading
    )

    return (
        <>
            <Form
                form={form}
                targetSubjectName={targetSubjectName}
                targetSubjectRole={targetSubjectRole}
                handleManagedAccessChange={handleManagedAccessChange}
                handleWellPermissionsChange={handleWellPermissionsChange}
                handleSelectAllWells={handleSelectAllWells}
            />
            {errorBanner.error && (
                <Alert
                    message={errorBanner.message}
                    alertType="danger"
                    isCloseable={false}
                />
            )}
            <ModalFooter
                advanceText="Submit"
                handleAdvanceClick={() => setShowConfirmationModal(true)}
                disableAdvance={disableSubmit}
            />
            <ConfirmationModal
                isOpen={showConfirmationModal}
                onConfirm={handleSubmitMutation}
                onCancel={() => {
                    setShowConfirmationModal(false)
                }}
                targetSubjectName={targetSubjectName}
                confirmAction={form.fullAccess ? "full" : "managed"}
            />
        </>
    )
}

export default FormContainer
